import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface CoiLink {
  id: string;
  companyId: string;
  companyName: string;
  companyCategory: string | null;
  note: string | null;
  createdAt: string;
}

export function useCoiLinks(coiId: string | undefined) {
  return useQuery<CoiLink[]>({
    queryKey: ["coi-links", coiId],
    queryFn: async () => {
      const r = await fetch(`/api/cois/${coiId}/links`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load links");
      return r.json();
    },
    enabled: !!coiId,
  });
}

export function useCoiLinkMutations(coiId: string | undefined) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["coi-links", coiId] });
    qc.invalidateQueries({ queryKey: ["coi-discovered", coiId] });
  };
  const add = useMutation({
    mutationFn: async (input: { companyId: string; note?: string }) => {
      const r = await fetch(`/api/cois/${coiId}/links`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Add failed");
      return r.json();
    },
    onSuccess: invalidate,
  });
  const updateNote = useMutation({
    mutationFn: async ({ linkId, note }: { linkId: string; note: string }) => {
      const r = await fetch(`/api/cois/${coiId}/links/${linkId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      });
      if (!r.ok) throw new Error("Update failed");
      return r.json();
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (linkId: string) => {
      const r = await fetch(`/api/cois/${coiId}/links/${linkId}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("Delete failed");
    },
    onSuccess: invalidate,
  });
  return { add, updateNote, remove };
}
